import React from 'react';

import useCurrentLang from '@/hooks/apps/useCurrentLang';

import {
  ABOUT_STRENGTH_PILLARS_BY_LOCALE,
  ABOUT_STRENGTH_TITLE_BY_LOCALE,
  ABOUT_WHY_SOUND_BY_LOCALE,
} from '@/data/shared/aboutCopy';

import Area from '@/components/apps/layouts/shared/Area';
import { AniSlideUp } from '@/components/shared/animations/basic';

import AboutSoundWave from './AboutSoundWave';
import ScrollRevealLines from './ScrollRevealLines';
import ScrollRevealText from './ScrollRevealText';

/**
 * ### AboutStrengthSection
 *
 * Why Deeply, answered without describing a product. The section asks why
 * sound at all before it claims anything about doing sound well: a reader
 * told that the company has worked on nonverbal sound since 2017, without
 * being told what is in a sound that cannot be written down, has no reason
 * to care that it was hard.
 *
 * So the argument runs in three moves: what the company chose, why that
 * choice is worth making, and what came of it. The three pillars are the
 * last of those, not the whole of it, and they are stacked rather than set
 * side by side so each is read as a claim instead of scanned as a column.
 *
 * Each pillar's statement lights word by word as it is scrolled to, which
 * paces it at reading speed, and the supporting lines under it fill one
 * sentence at a time, since each is a separate claim and breaking one in
 * the middle would read as a stutter.
 *
 * The headings, the numbers and the paragraph under the section title do
 * not move: they are the structure a reader scans to find their place, and
 * a structure that fades in is one that cannot be scanned.
 *
 * Each pillar is one statement set large with its support underneath, rather
 * than three bullets of equal weight: a reader who takes only the statement
 * away still has the point. The numerals are drawn oversized behind each
 * pillar so the three read as a sequence rather than as a row of boxes.
 *
 * On a phone the two halves of a pillar fall under one another, so the
 * padding that separates them side by side would only push the statement
 * away from the title it belongs to: there the columns close up and the
 * gap between pillars alone does the separating.
 *
 * The supporting lines are set in brandGray600 rather than the dimmer 400:
 * on this near-black ground that is about 9:1 against the background, which
 * is what the text needs to be read rather than merely seen. The hover wash
 * is barely there for the same reason — it marks the column under the
 * pointer without lifting the ground the type sits on.
 *
 * @returns The rendered section element
 * @see {@link ABOUT_STRENGTH_PILLARS_BY_LOCALE} The copy rendered here
 */
export default function AboutStrengthSection() {
  const currentLang = useCurrentLang();
  const { title, description } = ABOUT_STRENGTH_TITLE_BY_LOCALE[currentLang];
  const whySound = ABOUT_WHY_SOUND_BY_LOCALE[currentLang];
  const pillars = ABOUT_STRENGTH_PILLARS_BY_LOCALE[currentLang];

  return (
    <section
      className="relative overflow-hidden bg-apps-brandGrayBlack"
      data-analytics-section="about_strength"
    >
      {/* A wash of the brand gradient, kept faint so the type stays the subject */}
      <div
        className="pointer-events-none absolute -left-[10%] top-0 h-[520px] w-[70%] rounded-full bg-[radial-gradient(circle,rgba(212,157,255,0.18),transparent_70%)] blur-3xl"
        aria-hidden="true"
      />
      <Area>
        <div className="relative flex flex-col gap-16 py-[96px] lg:gap-24 lg:py-[clamp(140px,12.5vw,200px)]">
          <div className="max-w-[900px]">
            <p className="text-[13px] font-bold uppercase leading-[140%] tracking-[0.65px] text-apps-corePrimary01 lg:text-[15px]">
              Technical Strength
            </p>
            <h2 className="mt-4 text-balance text-[30px] font-bold leading-[134%] tracking-[-0.6px] text-apps-coreGrayWhite sm:text-[36px] lg:mt-6 lg:text-[clamp(42px,3.611vw,56px)] lg:leading-[126%] lg:tracking-[-1.12px]">
              {title}
            </h2>
            <p className="mt-6 text-pretty text-[16px] font-medium leading-[170%] tracking-[-0.32px] text-apps-brandGray800 lg:mt-8 lg:text-[clamp(17px,1.389vw,20px)]">
              {description}
            </p>
          </div>

          {/*
           * The premise. Everything around it is set left; this is centred,
           * which is what makes the page stop here rather than read on. The
           * waveform above it is the claim made visible — a written line
           * holds only so much — and the question is set in the brand
           * gradient at the scale of the section heading, because it is the
           * question the whole section exists to answer.
           */}
          <div className="flex flex-col items-center gap-10 text-center lg:gap-12">
            <span className="w-full max-w-[760px] text-apps-corePrimary01">
              <AboutSoundWave />
            </span>
            <div>
              <p className="bg-[linear-gradient(99deg,#ff6f80_0%,#ff3d59_45%,#e31b42_100%)] bg-clip-text text-balance text-[32px] font-bold leading-[130%] tracking-[-0.64px] [-webkit-background-clip:text] [-webkit-text-fill-color:transparent] sm:text-[40px] lg:text-[clamp(48px,4.167vw,64px)] lg:leading-[122%] lg:tracking-[-1.28px]">
                {whySound.question}
              </p>
              <ScrollRevealText className="mx-auto mt-7 max-w-[760px] text-pretty text-[17px] font-medium leading-[175%] tracking-[-0.34px] text-apps-brandGray900 lg:mt-9 lg:text-[clamp(19px,1.528vw,22px)]">
                {whySound.answer}
              </ScrollRevealText>
            </div>
          </div>

          {/*
           * Stacked rather than set in three columns. Side by side the three
           * were read as one comparison to be scanned; one under another,
           * each gets the width to be read as its own claim, and the number
           * and the title carry the left while the argument runs down the
           * right.
           */}
          <AniSlideUp>
            <ol className="flex flex-col gap-20 lg:gap-32">
              {pillars.map((pillar, index) => (
                <li
                  key={pillar.id}
                  className="group grid gap-6 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] lg:gap-16 lg:py-16 lg:last:pb-0"
                >
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center">
                      <span className="font-Sans text-2xl font-bold leading-none text-apps-brandGray800">
                        {String(index + 1).padStart(2, '0')}
                      </span>
                    </div>
                    <h3 className="text-balance text-[34px] font-bold leading-[130%] tracking-[-0.56px] text-apps-coreGrayWhite sm:text-[42px] lg:text-[clamp(48px,2.778vw,54px)]">
                      {pillar.title}
                    </h3>
                  </div>
                  <div className="lg:pt-10">
                    <ScrollRevealText className="text-pretty text-[22px] font-semibold leading-[160%] tracking-[-0.38px] text-apps-brandGray900 sm:text-[26px] lg:text-[clamp(32px,1.667vw,40px)] lg:leading-[165%]">
                      {pillar.lead}
                    </ScrollRevealText>
                    <ScrollRevealLines
                      className="mt-5 flex flex-col pt-5 lg:mt-9 lg:pt-9"
                      lineClassName="text-pretty text-[15px] font-medium leading-[170%] tracking-[-0.3px] text-apps-brandGray800 lg:text-[17px]"
                      lines={pillar.points}
                    />
                  </div>
                </li>
              ))}
            </ol>
          </AniSlideUp>
        </div>
      </Area>
    </section>
  );
}
